import React, { useState, useEffect, useRef, useMemo } from "react";
import { ContentService } from "../../services";
import LoadingScreen from "../../common/components/loadingScreen";
import { BiDislike, BiDotsHorizontalRounded, BiLike } from "react-icons/bi";
import { history } from "../../managers/history";
import { useSelector } from "react-redux";
import { sessionManager } from "../../managers/sessionManager";
import {
  keyTypeConstants,
  errorMessagesConstants,
  likeDislikeStatus,
  toastType,
} from "../../constants";
import Utils from "../../utility";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Toaster } from "react-hot-toast";
import { useForm } from "react-hook-form";
import ShowToast from "../../common/components/showToast";
import SpeechRecognition, {
  useSpeechRecognition,
} from "react-speech-recognition";
import { useSpeechSynthesis } from "react-speech-kit";
import { Modal, Typography, Backdrop } from "@mui/material";
import CustomModal from "../../common/customModal";
import FeedBackModal from "./../modal/feedBackModal";
import likeDisLikeHandler from "../../common/likeDisLikeHandler";
import AttachmentInput from "../../common/attachment";

const { setDataInCookies } = sessionManager;
const { APP_ID, REFERRED, CHATWIDGETUSERINFO } = keyTypeConstants;
const conversationId = `${Math.random()
  .toString(16)
  .slice(2, 10)
  .toUpperCase()}`;

// Function to check microphone permission and availability
const checkMicrophonePermission = async () => {
  try {
    // Check if we're in an iframe context
    const isInIframe = window !== window.top;

    // Check basic browser support
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      console.warn("MediaDevices API not supported");
      return { available: false, error: "MediaDevices API not supported" };
    }

    // Check if we're in iframe and warn about potential restrictions
    if (isInIframe) {
      console.warn(
        "Running in iframe context - microphone access may be restricted"
      );
    }

    // Try to get microphone permission
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

    // Clean up the stream immediately
    stream.getTracks().forEach((track) => track.stop());

    return { available: true, error: null };
  } catch (error) {
    console.error("Microphone permission check failed:", error);

    let errorMessage = "Microphone access denied";
    if (error.name === "NotAllowedError") {
      errorMessage = "Microphone permission denied by user";
    } else if (error.name === "NotFoundError") {
      errorMessage = "No microphone found";
    } else if (error.name === "NotSupportedError") {
      errorMessage = "Microphone not supported in this context";
    }

    return { available: false, error: errorMessage };
  }
};

const ChatWidget = ({ match }) => {
  const [botDetails, setBotDetails] = useState({});
  const [query, setQuery] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [responseLoading, setResponseLoading] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [uploadedFileUrls, setUploadedFileUrls] = useState([]);
  const [messages, setMessages] = useState([]);
  const [answer, setAnswer] = useState("");
  const [result, setResult] = useState("");
  const [isStreaming, setIsStreaming] = useState(false);
  const [getAppId, setGetAppId] = useState("");
  const [popUp, setPopUp] = useState(true);
  const [userInfoLoading, setUserInfoLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const chatUserInfo = sessionManager.getDataFromCookies(CHATWIDGETUSERINFO);
  const textareaRef = useRef(null);
  const [sse, setSse] = useState(null);
  const [chatUser, setChatUser] = useState({
    name: "",
    email: "",
    phoneNumber: "",
    chatUserId: "",
  });
  const [like, setLike] = useState([]);
  const [dislike, setDislike] = useState([]);
  const [isSpeechActive, setIsSpeechActive] = useState({
    index: null,
    value: false,
  });
  const [microphoneAvailable, setMicrophoneAvailable] = useState(false);
  const [microphoneError, setMicrophoneError] = useState("");
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();

  const [preResult, setPreResult] = useState("");
  const [isVoiceQuery, setIsVoiceQuery] = useState(false);
  const [isFeedBackModal, setIsFeedBackModal] = useState(false);
  const [isChatWidget, setIsChatWidget] = useState(true);
  const [selectedQueryId, setSelectedQueryId] = useState("");
  const url = new URL(window.location.href);
  const params = new URLSearchParams(url.search);
  const userType = params.get("usertype") || "";

  /**@start voice to text conversion */
  const {
    transcript,
    listening,
    resetTranscript,
    browserSupportsSpeechRecognition,
  } = useSpeechRecognition();

  useEffect(() => {
    if (!isChatWidget) return;
    setIsVoiceQuery(false);
    setQuery((prevText) => transcript);
    if (!browserSupportsSpeechRecognition) {
      return <span>Browser doesn't support speech recognition.</span>;
    }
    if (!listening && transcript) {
      setIsVoiceQuery(true);
      onSend(transcript);
    }
  }, [transcript, listening]);
  /**@end voice to text conversion */

  /**@start  text to speech conversion */
  const { speak, cancel, speaking } = useSpeechSynthesis();
  const handleTextToSpeech = () => {
    if (
      result.includes("!") ||
      result.includes(".") ||
      result.includes("?") ||
      result.includes(",") ||
      result.includes(":")
    ) {
      if (preResult.length > 0) {
        let splitRes = result.split(preResult);
        if (
          splitRes[1]?.includes("!") ||
          splitRes[1]?.includes(".") ||
          splitRes[1]?.includes("?") ||
          splitRes[1]?.includes(",") ||
          splitRes[1]?.includes(":")
        ) {
          speak({ text: splitRes[1] });
          setPreResult(splitRes[1]);
        }
      } else {
        speak({ text: result });
        setPreResult(result);
      }
    }
  };

  useEffect(() => {
    if (result && isVoiceQuery) {
      handleTextToSpeech();
    }
  }, [result]);

  /**@end text to speech conversion */

  /**@start  speech on speaker icon click */
  const handleSpeak = (index, content) => {
    if (speaking) {
      setIsSpeechActive({ index, value: false });
      cancel();
    } else {
      setIsSpeechActive({ index, value: !isSpeechActive.value });
      speak({ text: content });
    }
  };
  /**@end speech on speaker icon click */

  window.$crisp.push(["do", "chat:hide"]);
  const scrollRef = useRef(null);
  const user = useSelector((state) => state.user);

  const disableAutoScroll = useRef(false);

  const lastElRef = useRef(null);
  const isInViewport = useIsInViewport(lastElRef);

  useEffect(() => {
    if (isInViewport) disableAutoScroll.current = false;
  }, [isInViewport]);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const tenantId = urlParams.get("tenantId");
    const name = urlParams.get("username");
    const email = urlParams.get("email");
    setChatUser({ name, email, phoneNumber: "", chatUserId: "" });
    if (user.isLoggedIn || tenantId) {
      getAppDetails(tenantId);
    } else {
      const pathParts = window.location.pathname.split("/");
      const id = pathParts[pathParts.length - 1];
      if (/^[a-f\d]{24}$/i.test(id)) setDataInCookies(id, APP_ID, 5);
      setDataInCookies("true", REFERRED, 5);
      if (!user.isLoggedIn) history.push("/");
    }

    const initializeMicrophone = async () => {
      if (browserSupportsSpeechRecognition) {
        const { available, error } = await checkMicrophonePermission();
        setMicrophoneAvailable(available);
        setMicrophoneError(error || "");

        if (!available) {
          console.warn("Microphone not available:", error);
        }
      } else {
        setMicrophoneError("Speech recognition not supported in this browser");
      }
    };

    initializeMicrophone();

    // eslint-disable-next-line
  }, [browserSupportsSpeechRecognition]);

  const saveAppDetails = async (appId) => {
    let requestData = {
      appId,
      tenantId: user?.tenantId,
    };
    let [error, response] = await Utils.parseResponse(
      new ContentService().saveKnowledgeBaseByReferId(requestData)
    );
    if (error || !response) return;
  };

  const getAppDetails = async (tenantId) => {
    const urlParams = new URLSearchParams(window.location.search);
    const pathName = window.location.pathname;
    const appId = pathName.split("/").pop();
    const isReferred = urlParams.get("referred");
    if (isReferred) await saveAppDetails(appId);
    if (!tenantId) {
      urlParams.append("tenantId", user.tenantId);
      const newUrl = `${window.location.pathname}?${urlParams.toString()}${
        window.location.hash
      }`;
      window.history.replaceState(null, "", newUrl);
    }
    let queryData = {
      tenantId: tenantId ? tenantId : user.tenantId,
    };
    try {
      setIsLoading(true);
      const response = await new ContentService().getApp(
        match?.params?.appId,
        queryData
      );
      setBotDetails(response);
      fetchQueriesAuto(
        response?._id,
        response?.tenantId,
        response.sampleQueries,
        response?.suggestedQueries
      );
      setGetAppId(response?._id);
    } catch (error) {
      history.push("/");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchQueriesAuto = async (
    appId,
    tenantId,
    sampleQueries,
    suggestedQueries
  ) => {
    const filteredQueries = suggestedQueries.filter((queryList) => {
      const lowerCaseQuery = queryList.toLowerCase();
      return !(
        lowerCaseQuery.startsWith("i'm sorry") ||
        lowerCaseQuery.startsWith("couldn")
      );
    });

    let totalQueryList = [...sampleQueries];
    if (filteredQueries.length) {
      totalQueryList.push(...filteredQueries);
    } else if (sampleQueries.length === 0) {
      totalQueryList.push("What is this about?");
    }

    if (totalQueryList.length >= 3) {
      console.log(
        "Perform action when totalQueryList length is greater than or equal to 3"
      );
    } else {
      let requestData = {
        appId: appId,
        tenantId: tenantId,
      };
      let [error, response] = await Utils.parseResponse(
        new ContentService().querySuggestions(requestData)
      );
      if (error || !response) {
        return;
      }
      if (
        response.length === 1 ||
        response[0].startsWith("I couldn") ||
        response[0].startsWith("I'm sorry")
      ) {
        return;
      }

      let requestObj = {
        appId: appId,
        tenantId: tenantId,
        suggestedQueries: [...(filteredQueries || []), ...response],
      };
      let [err, res] = await Utils.parseResponse(
        new ContentService().updateSuggestions(requestObj)
      );
      if (err || !res) {
        return;
      }
      setBotDetails(res);
    }
  };

  const onSend = async () => {
    if (responseLoading || isStreaming) return;
    if (textareaRef.current) {
      textareaRef.current.style.height = "36px";
    }
    if (!query) return;
    setResponseLoading(true);

    const urlParams = new URLSearchParams(window.location.search);
    const tenantId = urlParams.get("tenantId");

    let response = "";

    const baseUrl = process.env.REACT_APP_QUERY_ENGINE_MICROSERVICE;

    try {
      setPreResult("");
      setResponseLoading(true);

      if (botDetails?.botType === "CONVERSATIONAL") {
        const userMessage = {
          role: "user",
          content: query,
          ...(uploadedFiles.length > 0 && { files: [...uploadedFiles] }),
        };
        const conversation = [...messages, userMessage];
        let userQandA =
          messages.length > 0
            ? messages.filter((eachQandA) => eachQandA.role === "user")
            : [];
        const assistantAnswers =
          messages.length > 0
            ? messages.filter((eachQandA) => eachQandA.role === "assistant")
            : [];
        if (userQandA.length >= 4) userQandA = userQandA.slice(1, 4);
        // eslint-disable-next-line
        let queryConversation;
        assistantAnswers.length > 0 &&
          (queryConversation = [{ role: "user", content: query }]);
        setMessages(conversation);
        setQuery("");
        setUploadedFiles([]);
        setUploadedFileUrls([]);
        const sendQueryId = true;
        const queryParams = new URLSearchParams({
          appId: match?.params?.appId,
          query,
          tenantId,
          sendQueryId,
          chatUser: JSON.stringify(chatUser),
          conversationId,
          ...(uploadedFileUrls.length > 0
            ? { files: uploadedFileUrls.join(",") }
            : {}),
        }).toString();
        setIsStreaming(true);

        const sse = new EventSource(`${baseUrl}/stream-query?${queryParams}`);
        setSse(sse);
        sse.addEventListener("message", ({ data }) => {
          setResponseLoading((prev) => (prev ? false : prev));
          const msg = JSON.parse(data);
          setResult((prev) => prev + msg);
          response += msg;
        });

        sse.addEventListener("error", ({ data = '""' }) => {
          setResponseLoading(false);
          const message = JSON.parse(data);
          console.log("error: ", message);
          const isEnded = sendQueryId
            ? message.startsWith("DONE-")
            : message === "DONE";
          if (sendQueryId && message === "DONE") return;
          if (!sendQueryId && message.startsWith("DONE-")) return;
          if (isEnded && response) {
            const resMessage = { role: "assistant", content: response };
            if (sendQueryId) {
              const [, queryId] = message.split("-");
              resMessage.queryId = queryId;
            }
            setMessages((prev) => [...prev, resMessage]);
            setResult("");
          } else {
            if (message === "limit reached") {
              ShowToast({
                message: errorMessagesConstants.QUERY_LIMIT_EXCEEDED,
                type: toastType.ERROR,
              });
            } else {
              ShowToast({
                message:
                  "Service Unavailable: The server is currently experiencing technical difficulties. Please try again later.",
                type: toastType.ERROR,
              });
            }
          }
          setIsStreaming(false);
          sse.close();
        });
      } else {
        if (answer) setAnswer("");
        setQuery("");

        setIsStreaming(true);
        const sendQueryId = true;
        const queryParams = new URLSearchParams({
          appId: match?.params?.appId,
          query,
          tenantId,
          sendQueryId,
          chatUser: JSON.stringify(chatUser),
          conversationId,
          ...(uploadedFileUrls.length > 0
            ? { files: uploadedFileUrls.join(",") }
            : {}),
        }).toString();
        const sse = new EventSource(`${baseUrl}/stream-query?${queryParams}`);
        setSse(sse);
        sse.addEventListener("message", ({ data }) => {
          setResponseLoading((p) => (p ? false : p));
          const msg = JSON.parse(data);
          setResult((r) => r + msg);
          response += msg;
        });

        sse.addEventListener("error", ({ data = '""' }) => {
          setResponseLoading(false);
          const message = JSON.parse(data);
          if (message === "DONE" && response) {
            setAnswer(response);
            setResult("");
          } else {
            if (message === "limit reached") {
              ShowToast({
                message: errorMessagesConstants.QUERY_LIMIT_EXCEEDED,
                type: toastType.ERROR,
              });
            } else {
              ShowToast({
                message:
                  "Service Unavailable: The server is currently experiencing technical difficulties. Please try again later.",
                type: toastType.ERROR,
              });
            }
          }
          sse.close();
          setIsStreaming(false);
          setUploadedFiles([]);
          setUploadedFileUrls([]);
        });
      }
    } catch (error) {
      ShowToast({
        message:
          "Service Unavailable: The server is currently experiencing technical difficulties. Please try again later.",
        type: toastType.ERROR,
      });
    }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && e.shiftKey) {
      setIsVoiceQuery(false);
      setQuery((prevQuery) => prevQuery);
    } else if (e.key === "Enter" && !e.shiftKey) {
      setIsVoiceQuery(false);
      e.preventDefault();
      onSend(query);
      setUploadedFiles([]);
      setUploadedFileUrls([]);
    }
  };
  const onStopGenerating = () => {
    if (sse) {
      sse.close();
      setIsVoiceQuery(false);
      setIsStreaming(false);
      setResponseLoading(false);
      const resMessage = { role: "assistant", content: result };
      setMessages((prev) => [...prev, resMessage]);
      setResult("");
    }
  };

  useEffect(() => {
    if (scrollRef.current && !disableAutoScroll.current) {
      scrollRef.current.scrollIntoView({ block: "end", behavior: "smooth" });
    }
  }, [messages, result]);

  if (isLoading) return <LoadingScreen />;

  const onSubmit = async (data) => {
    const urlParams = new URLSearchParams(window.location.search);
    const tenantId = urlParams.get("tenantId");
    try {
      const requestObj = {
        tenantId: tenantId,
        appId: getAppId,
        name: data.name,
        email: data.email,
        phoneNumber: data.phoneNumber,
      };
      setUserInfoLoading(true);
      let response = await new ContentService().chatUserInfo(requestObj);
      setChatUser({
        name: response.name,
        email: response.email,
        phoneNumber: response.phoneNumber,
        chatUserId: response._id,
      });
      setDataInCookies(response?._id, CHATWIDGETUSERINFO, 1);
      ShowToast({
        message: "User info successfully submit",
        type: toastType.SUCCESS,
      });
      setPopUp(false);
      setUserInfoLoading(false);
    } catch (error) {
      console.log(error);
      setUserInfoLoading(false);
      ShowToast({
        message: "Please try again",
        type: toastType.ERROR,
      });
    }
  };
  const handleFileSelection = (newFiles) => {
    const totalFiles = [...uploadedFiles, ...newFiles].slice(0, 3);
    setUploadedFiles(totalFiles);

    newFiles.forEach((file) => {
      if (uploadedFiles?.length < 3) {
        onFileChange(file);
      }
    });
  };

  const removeFile = (index) => {
    const updatedFiles = uploadedFiles.filter((_, i) => i !== index);
    setUploadedFiles(updatedFiles);

    const updatedUrls = uploadedFileUrls.filter((_, i) => i !== index);
    setUploadedFileUrls(updatedUrls);
  };
  const onFileChange = async (file) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append(
      "fileKey",
      `users-documents/${user?.tenantId}/${botDetails?._id}/${conversationId}/${file?.name}`
    );
    try {
      const response = await new ContentService().actionStepsConfigure(
        formData
      );
      setUploadedFileUrls((urls) => [...urls, response?.url]);
    } catch (error) {
      console.error("Failed to upload file:", error);
      ShowToast({
        message: "Failed to upload file. Please try again.",
        type: toastType.ERROR,
      });
    }
  };
  const welcomeMessage = botDetails?.welcomeMessage || "Hi👋";

  let pageTitle = botDetails?.pageTitle;
  let name = botDetails?.name;

  let titleContent;
  if (pageTitle && pageTitle.length > 7) {
    titleContent = pageTitle;
  } else if (!pageTitle && name && name.length > 7) {
    titleContent = name;
  } else {
    titleContent = pageTitle
      ? pageTitle
      : name && name.length > 20
      ? `${name.slice(0, 20)}...`
      : name;
  }

  let buttonContent;

  const autoGrow = (element) => {
    element.style.height = "36px";
    element.style.height = element.scrollHeight + "px";
  };

  if (responseLoading || isStreaming) {
    buttonContent = (
      <img
        src="/images/loading.svg"
        className="animate-spin mx-auto w-6 h-6 text-blue-20 right-3 top-4 my-auto mb-4 ml-1 "
      />
    );
  } else {
    buttonContent = (
      <img
        className="right-3 top-0 mb-4 my-auto ml-1 cursor-pointer"
        alt=""
        src="/images/send-msg-widget.svg"
      />
    );
  }

  /**
   * Refactored version of handleLikeHandler function.
   *
   * @param {string} status - The status of the event.
   * @param {string} queryId - The ID of the query.
   * @return {Promise<void>} A promise that resolves when the UI is updated.
   */
  const handleLikeHandler = async (status, queryId) => {
    // Get the tenantId from the URL query parameter
    const urlParams = new URLSearchParams(window.location.search);
    const tenantId = urlParams.get("tenantId");
    const payload = {
      status,
      tenantId,
      queryId,
      isLikeHandler: true,
    };
    const response = await likeDisLikeHandler(payload);

    // Update the UI based on the response status
    if (response.status === "Closed") {
      // Add the queryId to the likes array
      setLike((prevLikes) => [...prevLikes, queryId]);

      // Remove the queryId from the dislikes array
      setDislike((prevDislikes) => prevDislikes.filter((id) => id !== queryId));
    }
  };

  /**
   * Handles the dislike event and performs some actions.
   *
   * @return {Promise<void>} No return value.
   */
  const handleDislikeHandler = async (queryId) => {
    setSelectedQueryId(queryId);
    setIsFeedBackModal(true);
    setIsChatWidget(false);
  };

  /**
   * Closes the feedback modal and sets the chat widget to be visible.
   */
  const feedBackCloseModalHandler = () => {
    setIsFeedBackModal(false);
    setIsChatWidget(true);
  };

  return (
    <>
      <CustomModal isOpen={isOpen} closeModal={() => setIsOpen(false)}>
        <div className="bg-white w-full h-full z-20">
          <div className="flex flex-col mt-6 h-screen">
            <div>
              <Typography className="text-black-0 font-inter font-semibold text-ft6 mt-2">
                &#9679;&nbsp;&nbsp; Create A Support Ticket
              </Typography>
              <p className="text-grey-414141 font-inter font-semibold text-ft6 -mt-1 ml-7">
                This is our preferred method of contact as creating a support
                ticket with URC allows us to review the issue prior to response.
              </p>
              <div className="text-grey-414141 font-inter font-semibold text-ft6 ml-7 flex">
                <span className="text-ft1 mt-1 mr-4">&#9675;</span>
                <p>
                  If you are a{" "}
                  <span className="text-black-0 font-bold">URC DEALER</span>{" "}
                  with access to the URC Dealer Portal, please use this link to
                  generate{" "}
                  <a
                    href="https://urcportal.com/Support/myticket"
                    target="_blank"
                    className="text-blue-70"
                  >
                    a SUPPORT TICKET
                  </a>
                </p>
              </div>
              <div className="text-grey-414141 font-inter font-semibold text-ft6 ml-7 flex">
                <span className="text-ft1 mt-1 mr-4">&#9675;</span>
                <p>
                  If you are a{" "}
                  <span className="text-black-0 font-bold">CONSUMER</span> and
                  have an issue, please use this link to create{" "}
                  <a
                    href="https://www.urc-automation.com/contact-us"
                    target="_blank"
                    className="text-blue-70"
                  >
                    a ticket
                  </a>
                </p>
              </div>
            </div>
            <div className="">
              <Typography className="text-black-0 font-inter font-semibold text-ft6 mt-3">
                &#9679;&nbsp;&nbsp; Call Technical Support
              </Typography>
              <p className="text-grey-414141 font-inter font-semibold text-ft6 -mt-1 ml-7">
                Our business hours are from 9AM-5PM EST (Mon-Fri)
              </p>
              <div className="text-grey-414141 font-inter font-semibold text-ft6 ml-7 flex">
                <span className="text-ft1 mt-1 mr-4">&#9675;</span>
                <p>
                  Call{" "}
                  <a href="tel:+18009010800" className="text-blue-70">
                    800-901-0800
                  </a>{" "}
                  to speak with a Technical Support Representative
                </p>
              </div>
            </div>
            <Typography className="text-black-0 font-inter font-semibold text-ft6 mt-3">
              &#9679;&nbsp;&nbsp; Al Chat Reference Number
            </Typography>
            <p className="text-grey-414141 font-inter font-semibold text-ft6 ml-7">
              Use this reference number to refer to your Chat Session
            </p>
            <div className="text-grey-414141 font-inter font-semibold text-ft6 ml-7 flex">
              <span className="text-ft1 mt-1 mr-4">&#9675;</span>
              <p>
                Your session code is:{" "}
                <span className="text-blue-70">{conversationId}</span>
              </p>
            </div>
            <div className="flex justify-center mt-5">
              <div
                className="flex rounded-3xl border border-grey-DEDEDE bg-white w-37.5 h-12"
                onClick={() => setIsOpen(false)}
              >
                <button className="ml-6 ">
                  <Typography className="text-ft5 font-normal font-inter">
                    Back to Chat
                  </Typography>
                </button>
              </div>
            </div>
          </div>
        </div>
      </CustomModal>
      <div className="flex h-full bg-white ">
        <div
          className={`flex flex-col md:flex-row w-full ${
            !chatUserInfo && botDetails?.chatUserInfo?.enable && popUp
              ? "h-full"
              : "h-screen"
          }`}
        >
          <div className="flex flex-col">
            <div
              className=" w-full h-55px flex px-4 z-10 fixed top-0  border-b border-grey-DEDEDE"
              style={{
                backgroundImage: `url('/images/header-bg.jpg')`,
                backgroundSize: "cover",
              }}
            >
              <div className="w-full flex items-center">
                {/* <div className=" text-ft2 font-PoppinsRegular  font-semibold flex">
                  <img
                    src={botDetails?.logo || "/images/zbrain-icon.svg"}
                    alt=""
                    className="h-10 py-2"
                  />
                </div> */}
                <div>
                  {titleContent && (
                    <div className="ml-2">
                      <h1 className="text-ft15 font-semibold text-white font-PoppinsRegular">
                        {titleContent}
                      </h1>
                    </div>
                  )}
                </div>
              </div>
              {/* <div onClick={() => {
                window.parent && window.parent.postMessage("closeWidget", '*')
              }} className=" text-ft2 font-PoppinsRegular  font-semibold flex" id="closeWidget">
                <img
                  src={"/images/closeWidget.svg"}
                  alt=""
                  className="h-10 py-2 mt-2"
                />
              </div> */}
            </div>
          </div>
          {!chatUserInfo &&
          botDetails?.chatUserInfo?.enable &&
          popUp &&
          !user?.isLoggedIn ? (
            <div className="w-full flex flex-col justify-center mt-20 items-center">
              <div className="font-PoppinsBold text-ft6 md:text-ft16 text-black-10  justify-center items-center flex">
                <img
                  className=" w-8 md:w-12 md:h-12 h-8 mr-1"
                  alt=""
                  src="/images/botIcon.svg"
                />{" "}
                ZBrain Help Bot
              </div>
              <div className="h-60per rounded-2xl lg:w-40per w-full   px-6 pt-4.5">
                <div className="h-full flex flex-col justify-between gap-10 md:gap-0">
                  <div>
                    <div className="mt-5">
                      <h1 className="font-InterSemiBold text-ft5 text-black-0 break-words">
                        {botDetails?.chatUserInfo?.title}
                      </h1>
                    </div>
                    <div className="flex flex-col mt-6.5 gap-6">
                      {botDetails?.chatUserInfo?.name && (
                        <div className="flex flex-col">
                          <label
                            className="font-InterMedium text-black-10 text-ft3 "
                            htmlFor=""
                          >
                            Full Name
                          </label>
                          <input
                            type="text"
                            placeholder="Enter your full name"
                            {...register("name", { required: true })}
                            className="outline-none border-grey-10 rounded-2.5 relative h-12   px-15px border  font-PoppinsRegular text-ft5 text-black-0 placeholder-grey-80"
                          />
                          {errors.name && (
                            <span className="text-red-50 font-PoppinsRegular text-ft1">
                              Please enter your name
                            </span>
                          )}
                        </div>
                      )}
                      {botDetails?.chatUserInfo?.email && (
                        <div className="flex-col flex">
                          <label
                            className="font-InterMedium text-black-10 text-ft3 "
                            htmlFor=""
                          >
                            Email
                          </label>
                          <input
                            type="email"
                            placeholder="Enter your email"
                            {...register("email", {
                              required: true,
                              pattern: /^\S+@\S+$/i,
                            })}
                            className="outline-none border-grey-10 rounded-2.5 relative h-12   px-15px border  font-PoppinsRegular text-ft5 text-black-0 placeholder-grey-80"
                          />
                          {errors.email && (
                            <span className="text-red-50 font-PoppinsRegular text-ft1">
                              Please enter a valid email
                            </span>
                          )}
                        </div>
                      )}
                      {botDetails?.chatUserInfo?.phoneNumber && (
                        <div className="flex flex-col">
                          <label
                            className="font-InterMedium text-black-10 text-ft3 "
                            htmlFor=""
                          >
                            Phone No.
                          </label>
                          <input
                            type="text"
                            placeholder="Enter your phone no."
                            {...register("phoneNumber", {
                              required: true,
                              pattern: /^[0-9]{10}$/,
                              maxLength: 10,
                            })}
                            className="outline-none border-grey-10 rounded-2.5 relative h-12   px-15px border  font-PoppinsRegular text-ft5 text-black-0 placeholder-grey-80"
                          />
                          {errors.phoneNumber &&
                            errors.phoneNumber.type === "required" && (
                              <span className="text-red-50 font-PoppinsRegular text-ft1">
                                Please enter your phone number
                              </span>
                            )}
                          {errors.phoneNumber &&
                            errors.phoneNumber.type === "pattern" && (
                              <span className="text-red-50 font-PoppinsRegular text-ft1">
                                Please enter a valid phone number
                              </span>
                            )}
                          {errors.phoneNumber &&
                            errors.phoneNumber.type === "maxLength" && (
                              <span className="text-red-50 font-PoppinsRegular text-ft1">
                                Phone number should not exceed 10 digits
                              </span>
                            )}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className=" flex flex-col pb-13px pt-10">
                    <button
                      onClick={handleSubmit(onSubmit)}
                      className=" border-none text-center bg-black-10 text-white font-InterMedium text-ft3 h-45px w-full rounded-2.5"
                    >
                      {userInfoLoading ? (
                        <BiDotsHorizontalRounded className="animate-pulse mx-auto w-10 h-8 text-white   my-auto " />
                      ) : (
                        "Continue"
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex flex-col justify-between h-full md:h-screen w-full bg-white  text-black-0">
              <div className="flex flex-col justify-between h-screen w-full  overflow-hidden">
                <div
                  onWheel={() => {
                    disableAutoScroll.current = false;
                  }}
                  className=" px-3 md:px-0 py-4 flex flex-col  w-full overflow-y-auto pt-18.5"
                >
                  <div
                    ref={scrollRef}
                    className="flex py-2 font-InterRegular webkit-smoothing break-word fit-width px-4 mb-3 rounded-tr-xl rounded-br-xl rounded-bl-xl bg-lightGrey-10"
                  >
                    <span className="whitespace-pre-line font-PoppinsMedium text-ft1_5">
                      {welcomeMessage}
                    </span>
                  </div>
                  {messages.map((message, idx) => (
                    <div
                      className={
                        "flex " +
                        (message?.role === "user" ? "justify-end" : "")
                      }
                    >
                      {message?.role === "user" ? (
                        <></>
                      ) : (
                        <img
                          className="ml-1 mr-2 cursor-pointer h-23px mt-1"
                          alt=""
                          src="/images/robot-black-new.png"
                        />
                      )}
                      <div
                        className={`flex flex-col font-InterRegular webkit-smoothing break-words fit-width px-4 py-2 mb-3 rounded-br-xl rounded-bl-xl ${
                          message?.role === "user"
                            ? "bg-blue-70 rounded-tl-xl text-white"
                            : "bg-lightGrey-10 rounded-tr-xl"
                        }`}
                        ref={messages?.length - 1 === idx ? scrollRef : null}
                      >
                        {/* Display uploaded files for user messages - ABOVE the message */}
                        {message?.role === "user" &&
                          message?.files &&
                          message?.files.length > 0 && (
                            <div className="flex flex-wrap gap-2 mb-3">
                              {message.files.map((file, fileIndex) => {
                                const fileExtension = file.name
                                  .split(".")
                                  .pop()
                                  .toUpperCase();
                                const fileName = file.name.substring(
                                  0,
                                  file.name.lastIndexOf(".")
                                );
                                const displayName =
                                  fileName.length > 15
                                    ? fileName.slice(0, 12) + "..."
                                    : fileName;

                                return (
                                  <div
                                    key={fileIndex}
                                    className="flex items-center bg-white border border-grey-10 rounded-lg px-3 py-2 max-w-48"
                                  >
                                    <div className="flex items-center flex-1">
                                      <div
                                        className="flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center mr-2"
                                        style={{ backgroundColor: "#000000" }}
                                      >
                                        <img
                                          src="/images/document-uploaded.svg"
                                          alt="Document"
                                          className="w-4 h-4"
                                        />
                                      </div>
                                      <div className="flex-1 min-w-0">
                                        <div
                                          className="text-xs font-medium text-black-0 truncate"
                                          title={file.name}
                                        >
                                          {displayName}
                                        </div>
                                        <div className="text-xs text-black-70 mt-0.5">
                                          {fileExtension}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}

                        <span
                          className={`whitespace-pre-line flex font-PoppinsMedium text-ft1_5`}
                        >
                          <ReactMarkdown
                            linkTarget="_blank"
                            children={message?.content}
                            remarkPlugins={[
                              [remarkGfm, { singleTilde: false }],
                            ]}
                            className="markdown"
                          />
                        </span>
                        {message.role === "assistant" && (
                          <div className="flex flex-row gap-2.5 mt-2 justify-start">
                            {userType !== "dealer" &&
                              userType !== "" &&
                              message.content !==
                                "Thank you for the chat. We are available 24/7 to help." && (
                                <>
                                  <BiLike
                                    className={` cursor-pointer w-4.5 h-4 ${
                                      like.includes(message?.queryId)
                                        ? "text-blue-64A2FE"
                                        : "text-grey-0"
                                    }`}
                                    onClick={() =>
                                      handleLikeHandler(
                                        likeDislikeStatus.CLOSED,
                                        message?.queryId
                                      )
                                    }
                                  />
                                  <BiDislike
                                    className={` cursor-pointer w-4.5 h-4 ${
                                      dislike.includes(message?.queryId)
                                        ? "text-blue-64A2FE"
                                        : "text-grey-0"
                                    }`}
                                    onClick={() =>
                                      handleDislikeHandler(message?.queryId)
                                    }
                                  />
                                  {FeedBackModal && (
                                    <FeedBackModal
                                      isOpen={isFeedBackModal}
                                      isChatWidget={isChatWidget}
                                      status={
                                        likeDislikeStatus.REQUIRE_ATTENTION
                                      }
                                      queryId={selectedQueryId}
                                      closeModal={feedBackCloseModalHandler}
                                      setDislike={setDislike}
                                      setLike={setLike}
                                      openContactModal={() => setIsOpen(true)}
                                    />
                                  )}
                                </>
                              )}

                            <img
                              className="ml-0.5 mr-2 cursor-pointer h-4"
                              alt=""
                              src={
                                isSpeechActive.index === idx &&
                                isSpeechActive.value &&
                                speaking
                                  ? "/images/bot-active-speaker-icon.svg"
                                  : "/images/bot-speaker-icon.svg"
                              }
                              onClick={() => handleSpeak(idx, message?.content)}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                  {!!result && (
                    <div
                      ref={scrollRef}
                      className="flex py-2 font-InterRegular webkit-smoothing break-words fit-width px-4 mb-3 rounded-tr-xl rounded-br-xl rounded-bl-xl bg-lightGrey-10"
                    >
                      <span className="whitespace-pre-line font-PoppinsMedium text-ft1_5">
                        <ReactMarkdown
                          linkTarget="_blank"
                          children={result}
                          remarkPlugins={[[remarkGfm, { singleTilde: false }]]}
                          className="markdown"
                        />
                      </span>
                    </div>
                  )}
                  <div ref={lastElRef}></div>
                </div>
                <div className="mt-auto">
                  <div className="flex pt-3.5 mt-auto md:mx-20per tab:mx-5 mobile:mx-4 justify-end  bg-white rounded-lg mb-0">
                    {responseLoading || isStreaming ? (
                      <div
                        className="flex items-center justify-end rounded-3xl border border-grey-DEDEDE bg-white p-2 my-2 mr-2"
                        onClick={onStopGenerating}
                      >
                        <div className=" text-ft2 font-PoppinsRegular  font-semibold flex">
                          <img
                            className="ml-1 mr-2 cursor-pointer"
                            alt=""
                            src="/images/rectangle.svg"
                          />
                        </div>
                        <div>
                          <div className="ml-2 mr-2">
                            <Typography className="text-ft2 font-medium font-InterRegular">
                              Stop generating
                            </Typography>
                          </div>
                        </div>
                      </div>
                    ) : null}
                    <div className="flex items-center justify-end rounded-3xl border border-grey-DEDEDE bg-white p-2 my-2 mr-2">
                      <div className="ml-2 mr-2">
                        <button
                          onClick={() => {
                            setMessages([
                              ...messages,
                              { role: "user", content: " I'm all set, thanks" },
                              {
                                role: "assistant",
                                content:
                                  "Thank you for the chat. We are available 24/7 to help.",
                              },
                            ]);
                          }}
                        >
                          <Typography className="text-ft2 font-medium font-InterRegular">
                            I'm all set, thanks
                          </Typography>
                        </button>
                      </div>
                    </div>
                    <div
                      className="flex items-center justify-end rounded-3xl border border-grey-DEDEDE bg-white p-2 my-2"
                      onClick={() => setIsOpen(true)}
                    >
                      <div className=" text-ft2 font-PoppinsRegular  font-semibold flex">
                        <img
                          src={"/images/call-icon.svg"}
                          alt=""
                          className="h-8 py-2 ml-2"
                        />
                      </div>
                      <div>
                        <div className="ml-2 mr-2 cursor-pointer">
                          <Typography className="text-ft2 font-medium font-InterRegular">
                            Contact
                          </Typography>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="bg-lightGrey-10 rounded-lg mb-3 relative md:mx-20per tab:mx-5 mobile:mx-4">
                    {uploadedFiles.length > 0 && (
                      <div className="flex flex-wrap gap-3 mb-2 p-5">
                        {uploadedFiles.map((file, index) => {
                          const fileExtension = file.name
                            .split(".")
                            .pop()
                            .toUpperCase();
                          const fileName = file.name.substring(
                            0,
                            file.name.lastIndexOf(".")
                          );
                          const displayName =
                            fileName.length > 20
                              ? fileName.slice(0, 17) + "..."
                              : fileName;

                          return (
                            <div
                              key={index}
                              className="relative flex items-center bg-white border border-grey-10 rounded-lg px-4 py-2 min-w-64 max-w-80"
                            >
                              <div className="flex items-center flex-1">
                                <div
                                  className="flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center mr-3"
                                  style={{ backgroundColor: "#000000" }}
                                >
                                  <img
                                    src="/images/document-uploaded.svg"
                                    alt="Document"
                                    className="w-6 h-6"
                                  />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div
                                    className="text-sm font-medium text-gray-900 truncate"
                                    title={file.name}
                                  >
                                    {displayName}
                                  </div>
                                  <div className="text-xs text-black-70 mt-0.5">
                                    {fileExtension}
                                  </div>
                                </div>
                              </div>
                              <button
                                onClick={() => removeFile(index)}
                                className="absolute -top-2 -right-2 w-5 h-5 flex items-center justify-center text-gray-400 hover:text-gray-600 text-lg font-medium"
                                disabled={responseLoading || isStreaming}
                                title="Remove file"
                              >
                                <img
                                  src="/images/close.svg"
                                  className="w-2.5 h-2.5"
                                ></img>
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    )}
                    {botDetails?.isAttachmentEnabled && (
                      <div className="absolute z-10">
                        <AttachmentInput
                          app={botDetails}
                          disabled={
                            responseLoading ||
                            isStreaming ||
                            uploadedFiles?.length >= 2
                          }
                          onSelect={handleFileSelection}
                          position="top-4.5"
                        />
                      </div>
                    )}

                    <div className="flex pt-3.5 justify-end">
                      <textarea
                        type="text"
                        ref={textareaRef}
                        onInput={(e) => autoGrow(e.target)}
                        value={query}
                        onKeyUp={onKey}
                        onChange={(e) => setQuery(e.target.value)}
                        className="max-h-75 resize-none w-full outline-none text-ft1_5 md:text-ft12 placeholder-grey-10 bg-transparent rounded-lg font-PoppinsRegular pl-10 pr-12"
                        disabled={responseLoading || isStreaming}
                      />

                      {microphoneAvailable &&
                      browserSupportsSpeechRecognition ? (
                        listening ? (
                          <button onClick={SpeechRecognition.stopListening}>
                            <img
                              className="right-10 mb-4 my-auto mr-4 cursor-pointer"
                              alt=""
                              src="/images/active-mick-icon.svg"
                            />
                          </button>
                        ) : (
                          <button
                            onClick={async () => {
                              try {
                                const { available, error } =
                                  await checkMicrophonePermission();
                                if (available) {
                                  SpeechRecognition.startListening();
                                } else {
                                  ShowToast({
                                    message:
                                      error ||
                                      "Microphone access denied. Please allow microphone permissions and try again.",
                                    type: toastType.ERROR,
                                  });
                                }
                              } catch (err) {
                                ShowToast({
                                  message:
                                    "Unable to access microphone. Please check your browser settings.",
                                  type: toastType.ERROR,
                                });
                              }
                            }}
                          >
                            <img
                              className="right-10 mb-4 my-auto mr-4 cursor-pointer"
                              alt=""
                              src="/images/mick-icon.svg"
                            />
                          </button>
                        )
                      ) : (
                        <button
                          onClick={() => {
                            if (!browserSupportsSpeechRecognition) {
                              ShowToast({
                                message:
                                  "Speech recognition is not supported in this browser. Please try using Chrome, Edge, or Safari.",
                                type: toastType.ERROR,
                              });
                            } else {
                              ShowToast({
                                message:
                                  microphoneError ||
                                  "Microphone access is not available. Please check your browser settings and permissions.",
                                type: toastType.ERROR,
                              });
                            }
                          }}
                          title={microphoneError || "Microphone not available"}
                        >
                          <img
                            className="right-10 mb-4 my-auto mr-4 cursor-pointer opacity-50"
                            alt=""
                            src="/images/mick-icon.svg"
                          />
                        </button>
                      )}

                      <div className="">
                        <img
                          className="top-2 right-14 mr-3 mb-2"
                          alt=""
                          src="/images/devider.svg"
                        />
                      </div>

                      <div className="mr-5 mt-2">
                        <button
                          onClick={() => {
                            setIsVoiceQuery(false);
                            onSend();
                          }}
                          disabled={responseLoading || isStreaming}
                        >
                          {buttonContent}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <Typography className="text-ft2 font-normal font-InterRegular md:mx-20per tab:mx-5 mobile:mx- mb-3 text-center">
                URC Virtual Assistant
                <a
                  className="text-blue-70 font-semiBold"
                  href="https://www.urc-automation.com/urcvirtualagreement"
                  target="_blank"
                >
                  &nbsp;Terms
                </a>
                &nbsp;and&nbsp;
                <a
                  className="text-blue-70 font-semiBold"
                  href="https://www.urc-automation.com/legal/privacy-policy"
                  target="_blank"
                >
                  Privacy Policy
                </a>
              </Typography>
              <Toaster
                toastOptions={{
                  className: " alert-toast font-PoppinsMedium",

                  style: {
                    borderRadius: "16px",
                  },
                }}
              />
            </div>
          )}
        </div>{" "}
      </div>
    </>
  );
};

export default ChatWidget;

function useIsInViewport(ref) {
  const [isIntersecting, setIsIntersecting] = useState(false);

  const observer = useMemo(
    () =>
      new IntersectionObserver(([entry]) =>
        setIsIntersecting(entry.isIntersecting)
      ),
    []
  );

  useEffect(() => {
    const interval = setInterval(() => {
      if (ref.current) {
        observer.observe(ref.current);
        clearInterval(interval);
      }
    }, 200);

    return () => {
      observer.disconnect();
    };
  }, [ref, observer]);

  if (!ref.current) return true;
  return isIntersecting;
}
